// @flow
/*
 * Unprotected Goods and Services has some weird analytic page names.
 */

import * as fpti from '../../lib/analytics/fpti'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { isEmail } from '../../../../shared/validators'
import { getPreferredFundingSource } from './funding-option-utils'
import { PAYMENT_TYPE, transactionTypeMap } from '../../../../lib/constants'
import store from '../stores/configureStore'

const pageActionMap = {
  // start of guest specific analytics
  guestSelectFF: {
    link: 'chooseFF',
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {
      fltp: 'f&f',
    },
  },
  guestSelectGS: {
    link: 'chooseGS',
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {
      fltp: 'g&s',
    },
  },
  guestRedirectTesla: {
    link: 'signUpAndCreateAcct',
    pageGroup: 'main:p2p:guest:start',
    pageName: 'main:p2p:guest:start::',
    extra: {},
  },
  guestSend: {
    link: 'Continue',
    pageGroup: 'main:p2p:guest:start',
    pageName: 'main:p2p:guest:start::',
  },
  guestReview: {
    link: 'Agree and send money',
    pageGroup: 'main:p2p:guest:preview',
    pageName: 'main:p2p:guest:preview::',
  },
  // end of guest specific analytics
  // pay request send button
  payRequestSend: {
    link: 'Send',
    pageGroup: 'main:p2p:payrequest::review',
    pageName: 'main:p2p:payrequest::review::',
    extra: {},
  },
  // establish set of canonical field names
  paymentChoice: {
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  sellerPaysFee: {
    link: 'sellerpays',
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  senderPaysFee: {
    link: 'buyercovers',
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  ffPreview: {
    pageGroup: 'main:p2p:unified:send::review',
    pageName: 'main:p2p:unified:send::review:node::',
    extra: {},
  },
  gsPreview: {
    pageGroup: 'main:p2p:unified:buy::review',
    pageName: 'main:p2p:unified:buy::review:node::',
    extra: {},
  },
  continue: {
    link: 'continue',
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  selectFF: {
    link: 'friendsnfamily',
    eventData: () => {
      const { recipient } = store.getState()

      return Events.p2p_payment_type_toggle_pressed({
        protection_design: recipient.moneyPageOverPanel,
        payment_type: 'personal',
        suggested_protection_design: recipient.suggestedDesign,
      })
    },
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  selectGS: {
    link: 'goodsnservices',
    type: 'cl',
    eventData: () => {
      const { recipient } = store.getState()

      return Events.p2p_payment_type_toggle_pressed({
        protection_design: recipient.moneyPageOverPanel,
        payment_type: 'purchase',
        suggested_protection_design: recipient.suggestedDesign,
      })
    },
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  onFFchangePaymentType: {
    link: 'change',
    pageGroup: 'main:p2p:unified:send::review',
    pageName: 'main:p2p:unified:send::review:node::',
    extra: {},
  },
  onGSchangePaymentType: {
    link: 'change',
    pageGroup: 'main:p2p:unified:buy::review',
    pageName: 'main:p2p:unified:buy::review:node::',
    extra: {},
  },
  onFFclickNoteField: {
    link: 'note',
    pageGroup: 'main:p2p:unified:send::review',
    pageName: 'main:p2p:unified:send::review:node::',
    extra: {},
  },
  onGSclickNoteField: {
    link: 'note',
    pageGroup: 'main:p2p:unified:buy::review',
    pageName: 'main:p2p:unified:buy::review:node::',
    extra: {},
  },
  onClickSendPaymentNow: {
    link: 'Send Payment Now',
    pageGroup: 'main:p2p::cards:authenticate_with_card_network',
    pageName: 'main:p2p::cards:authenticate_with_card_network::',
    extra: {},
  },
  purchaseProtection: {
    link: 'purchaseprotection',
    eventData: () => {
      const { recipient } = store.getState()

      return Events.p2p_payment_type_terms_pressed({
        protection_design: recipient.moneyPageOverPanel,
        suggested_protection_design: recipient.suggestedDesign,
      })
    },
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
  // checkbox variant
  enableProtection: {
    link: 'changeEnabled',
    pageGroup: 'main:p2p:unified:buy::review',
    pageName: 'main:p2p:unified:buy::review:node::',
    extra: {},
  },
  disableProtection: {
    link: 'changeDisabled',
    pageGroup: 'main:p2p:unified:send::review',
    pageName: 'main:p2p:unified:send::review:node::',
    extra: {},
  },
  paymentChoicePanelSend: {
    pageGroup: 'main:p2p:unified:flow:send:option',
    pageName: 'main:p2p:unified:flow:send:option:node::',
    extra: {},
  },
  paymentChoicePanelBuy: {
    pageGroup: 'main:p2p:unified:flow:buy:option',
    pageName: 'main:p2p:unified:flow:buy:option:node::',
    extra: {},
  },
  eligiblePurchases: {
    link: 'eligiblepurchases',
    pageGroup: 'main:p2p:unified:flow::option',
    pageName: 'main:p2p:unified:flow::option:node::',
    extra: {},
  },
}

export function getUnifiedSendAnalyticInfo(pageAction: string) {
  return pageActionMap[pageAction]
}

export function trackImpressionUnifiedSend(
  pageAction: string,
  extraAnalytics: any = {}
) {
  const { pageGroup, pageName, extra } = pageActionMap[pageAction]
  fpti.trackImpression(pageGroup, pageName, {
    ...extra,
    flfr: window.flfr,
    ...extraAnalytics,
  })
}

export function trackCancelBtn() {
  const { recipient } = store.getState()
  tracking(
    Events.p2p_payment_type_cancel_pressed({
      suggested_protection_design: recipient.suggestedDesign,
      protection_design: recipient.moneyPageOverPanel,
    })
  )
  fpti.trackLinkWithData(
    'cancel',
    'main:p2p:unified:flow::option',
    'main:p2p:unified:flow::option:node::',
    {},
    'link',
    {}
  )
}

export function getPageGroup(
  flowType: string,
  recipient: string,
  isReview: boolean
) {
  if (flowType === 'payRequest') {
    return `main:p2p:payrequest::review`
  }

  return `main:p2p:unified:${flowType === 'buy' ? 'buy' : 'send'}::review`
}

export function getPageName(
  flowType: string,
  recipient: string,
  isReview: boolean
) {
  if (isReview) {
    if (flowType === 'payRequest') {
      return `main:p2p:payrequest::review:node::`
    }

    return `main:p2p:unified:${
      flowType === 'buy' ? 'buy' : 'send'
    }::review:node::`
  }
  return getAmountPageTrackingStrings(flowType, recipient).pageName
}

export function getAmountPageTrackingStrings(
  flowType: string,
  recipient: string
) {
  const emailOrPhone = isEmail(recipient) ? 'email' : 'phone'
  const pageGroup = `main:p2p:unified:${flowType}::${emailOrPhone}`
  const pageName = `main:p2p:unified:${flowType}::${emailOrPhone}:node::`

  return {
    pageGroup,
    pageName,
  }
}

export function trackAmountPageImpression(
  flowType: string,
  recipient: string,
  data: any = {}
) {
  /* This is to track the recurring type for protections experiment. This
    does not include the external flow.
  */
  const { external, recipient: receiver } = store.getState()
  if (!external.env && receiver) {
    data.recurring_type = receiver.recurringType || 'none'
    data.rcvr_acct_type = receiver.recipientAccountType || ''
  }

  const { pageGroup, pageName } = getAmountPageTrackingStrings(
    flowType,
    recipient
  )
  fpti.trackImpression(pageGroup, pageName, data)
}

export function trackAmountPageClick(
  flowType: string,
  recipient: string,
  linkName: string
) {
  const { pageGroup, pageName } = getAmountPageTrackingStrings(
    flowType,
    recipient
  )
  fpti.trackLinkWithData(linkName, pageGroup, pageName, {}, 'link', {})
}

export function trackClickUnifiedSend(
  pageAction: string,
  extraData: Object = {}
) {
  const { link, pageGroup, pageName, extra, eventData } = pageActionMap[
    pageAction
  ]
  if (typeof eventData === 'function') {
    // eventData property recently added for mParticle data or any other future tracking sdk
    tracking(eventData())
  }
  fpti.trackLinkWithData(
    link,
    pageGroup,
    pageName,
    Object.assign(extra, extraData),
    'link',
    {}
  )
}

export function trackClick3ds2(page: string, extraAnalytics: any = {}) {
  const { link, pageGroup, pageName } = pageActionMap[page]
  fpti.trackLinkWithData(link, pageGroup, pageName, extraAnalytics, 'link', {})
}

/* Track payment choice, page and pgrp changes depending on current page
 * Please see section titled: Clicking on 'Change Payment Type' button
 * https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=375228475
 */
export function trackGuestClickPmtChoice() {
  const pageName = location.pathname.endsWith('/preview')
    ? 'main:p2p:guest:preview:'
    : 'main:p2p:guest:start:::'

  const pageGroup = location.pathname.endsWith('/preview')
    ? 'main:p2p:guest:preview'
    : 'main:p2p:guest:start'

  const linkName = 'ChangePmtType'

  fpti.trackLinkWithData(linkName, pageGroup, pageName, {}, 'link', {})
}

/* Tracks when guest returns to page after selecting payment choice
 * Please see section titled: Return to Guest Start Page
 * https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=375228475
 *
 */
export function trackGuestReturnToPagePmtChoice(pageAction: string) {
  const { pageGroup, link, extra } = pageActionMap[pageAction]
  const pageLink = `${pageGroup}|${link}`

  const newPageName = location.pathname.endsWith('/preview')
    ? 'main:p2p:guest:preview:'
    : 'main:p2p:guest:start:::'
  const newPageGroup = location.pathname.endsWith('/preview')
    ? 'main:p2p:guest:preview'
    : 'main:p2p:guest:start'

  const data = {
    pglk: pageLink,
  }

  if (extra.fltp) {
    fpti.setPersistentBeaconData('fltp', extra.fltp)
  }

  fpti.trackImpression(newPageGroup, newPageName, data)
}

export function getAnalytics({
  selectedFundingOption,
  fundingOptions,
  flowType = '',
}: {
  selectedFundingOption: FundingOption,
  fundingOptions: FundingOption[],
  flowType: string,
}) {
  const preferred = getPreferredFundingSource(fundingOptions) || { id: '' }
  const selectedFOExists = !!Object.keys(selectedFundingOption).length
  const dflFmxId =
    selectedFOExists &&
    selectedFundingOption.funding_sources.map(source => source.id)
  const usingPreferred =
    selectedFOExists &&
    selectedFundingOption.funding_sources.some(source => source.isPreferred)

  const paymentType = transactionTypeMap[flowType]
  const fltp = paymentType === PAYMENT_TYPE.PURCHASE ? 'g&s' : 'f&f'
  const dfl_fmxid = dflFmxId ? dflFmxId.toString() : 'nodefault'
  const pref_fmxid = preferred.id || 'notset'
  const is_pref = usingPreferred ? 'Y' : 'N'
  // indicating user lands on preview page without coming from funding option selection page
  const flfr = 'start'

  return { fltp, dfl_fmxid, pref_fmxid, is_pref, flfr }
}
